import { Coins } from 'lucide-react';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { InfoMessage } from '@/components/drawer/InfoMessage';
import { SummaryStrip } from '@/components/drawer/SummaryStrip';
import { HistoryCard } from '@/components/history/HistoryCard';
import { RecordLink } from '@/components/links/RecordLinks';
import { TextBadge } from '@/components/StatusBadge';
import { useMemberBalanceQuery } from '@/api/query/balance';
import { useRefreshPolicyData } from '@/api/query/refresh';
import type { BalanceEntry, Member } from '@/types';
import type { DrawerMember } from '@/contexts/DrawerContext';
import { formatCurrency, formatPaymentDate } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';
import { BALANCE_RATE } from '@/lib/balances';
import { memberHeading } from '@/lib/utils';
import { BalancePaymentForm } from './BalancePaymentForm';

interface MemberBalanceDrawerProps {
  member: Member | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EntryDetails({ member, entry }: { member: DrawerMember; entry: BalanceEntry }) {
  return (
    <div>
      {entry.kind === 'add' ? 'Paid by PMS' : entry.source === 'policy_close' ? 'Policy close' : `Balance payment · ${entry.paymentType ?? ''}`}
      {entry.refNo && (
        <>
          {' · '}
          <b>
            <RecordLink member={member} refNo={entry.refNo} />
          </b>
        </>
      )}
      {entry.kind === 'add' && entry.note && <p className="text-xs text-gray-500">{entry.note}</p>}
      {entry.kind === 'repay' && entry.toInterest > 0 && (
        <p className="text-xs text-gray-500">
          {formatCurrency(entry.toInterest)} interest + {formatCurrency(entry.toPrincipal)} balance
        </p>
      )}
    </div>
  );
}

/**
 * Member Balance drawer: what PMS paid for the member (Paid by PMS), the 12% interest on it,
 * repayments, and a form to record a new repayment.
 */
export function MemberBalanceDrawer({ member, open, onOpenChange }: MemberBalanceDrawerProps) {
  const refreshPolicyData = useRefreshPolicyData();
  const { data: balance, isLoading } = useMemberBalanceQuery(member?.id, open);
  const subtitle = member ? `${memberHeading(member)}` : '';

  const handleSaved = async () => {
    await refreshPolicyData();
    onOpenChange(false);
  };

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange} title="Member Balance" member={member}>
      {isLoading && <p className="text-sm text-gray-500">Loading balance...</p>}
      {balance && (
        <>
          <DrawerSection
            icon={Coins}
            title="Balance Status"
            description={`${BALANCE_RATE * 100}% per year on what's left, added every month · payments clear interest first`}
            aside={<TextBadge tone={balance.balanceDue > 0 ? 'amber' : 'teal'}>{balance.balanceDue > 0 ? 'Balance Due' : 'No Balance'}</TextBadge>}
          >
            <SummaryStrip
              items={[
                { label: 'Paid by PMS', value: formatCurrency(balance.added) },
                { label: 'Interest Charged', value: formatCurrency(balance.interestCharged) },
                { label: 'Repaid', value: formatCurrency(balance.repaid), tone: 'text-teal-800' },
                { label: 'Balance Due', value: formatCurrency(balance.balanceDue), tone: 'text-amber-900' },
              ]}
            />
          </DrawerSection>

          {balance.balanceDue > 0 ? (
            <BalancePaymentForm key={balance.memberId} balance={balance} onSaved={handleSaved} />
          ) : (
            <DrawerSection>
              <InfoMessage>
                No balance due. When a policy or loan payment is saved as <b>Paid by PMS</b>, the amount is added here.
              </InfoMessage>
            </DrawerSection>
          )}

          <HistoryCard
            key={balance.memberId}
            title="Member Balance"
            subtitle={subtitle}
            tabs={[
              {
                id: 'entries',
                label: 'Balance History',
                empty: 'No balance history yet.',
                columns: [{ label: 'Date' }, { label: 'Details' }, { label: 'Added' }, { label: 'Repaid' }, { label: 'Balance' }],
                rows: balance.entries.map((entry) => ({
                  key: entry.id,
                  cells: [
                    formatPaymentDate(entry.date),
                    <EntryDetails key="details" member={{ id: balance.memberId, name: balance.memberName, memberNo: balance.memberNo }} entry={entry} />,
                    entry.kind === 'add' ? formatCurrency(entry.amount) : '--',
                    entry.kind === 'repay' ? <span key="repaid" className="text-teal-800">{formatCurrency(entry.amount)}</span> : '--',
                    <b key="after">{formatCurrency(entry.balanceAfter)}</b>,
                  ],
                })),
              },
              {
                id: 'interest',
                label: 'Interest History',
                empty: 'No interest yet. Interest starts the month after the first Paid by PMS amount.',
                columns: [
                  { label: 'Month' },
                  { label: 'Balance' },
                  { label: 'Interest (12% / yr)' },
                  { label: 'Total Interest' },
                  { label: 'Balance Due' },
                ],
                rows: balance.interest.map((row) => ({
                  key: row.month,
                  cells: [
                    formatMonthKey(row.month),
                    formatCurrency(row.balance),
                    formatCurrency(row.interest),
                    formatCurrency(row.totalInterest),
                    <b key="due">{formatCurrency(row.balanceDue)}</b>,
                  ],
                })),
              },
            ]}
          />
        </>
      )}
    </SideDrawer>
  );
}
